import React, { useMemo } from 'react';

import {
  useReactTable,
  getCoreRowModel,
  CellContext,
  RowData,
  Row,
  Updater,
  SortingState,
} from '@tanstack/react-table';

import { Box, Text } from '@adsk/alloy-react-box';
import Checkbox from '@adsk/alloy-react-checkbox';
import { keyBy } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import {
  useTable,
  TableRow,
  TableCell,
  TableHeader,
  TableHeaderGroup,
  LoadMoreDataFooter,
} from '@adsk/alloy-react-table';
import { type StyleProp } from '@adsk/alloy-react-theme';

import ReferencePickerPlugin from '../../../types/ReferencePickerPlugin';
import {
  SORT_ORDER,
  SUBMITTAL_ENTITIY_DOMAIN,
  SUBMITTAL_ENTITIY_TYPE,
  TABLE_COLUMN_ID_KEY,
  ENTITIES_MAPPER_KEY,
} from '../consts';
import {
  useCustomAttributes,
  useTableColumns,
  useVirtualTable,
} from '../hooks';
import { useTranslation } from '../i18n';
import { SubmittalsItem } from '../types';
import { getSortFieldId } from '../utils/customFields';

const STYLES: {
  LoadMoreDataRow: StyleProp;
  StickyHeader: React.CSSProperties;
  TableStyle: (width: number) => React.CSSProperties;
} = {
  LoadMoreDataRow: { paddingLeft: '50px', justifyContent: 'flex-start' },
  StickyHeader: { position: 'sticky', margin: 0, top: 0, zIndex: 1 },
  TableStyle: (width: number) => ({
    width,
    borderSpacing: 0,
    textAlign: 'left',
  }),
};

interface TemplateCategories {
  name: string;
  templates: string[];
}

interface DataViewProps {
  sourceEntityId?: string;
  sourceEntityDomain?: string;
  sourceEntityType?: string;
  templateCategories?: TemplateCategories[];
}

function extractDataViewProps(opts?: DataViewProps) {
  if (opts) {
    const sourceEntityId =
      opts.sourceEntityId === undefined ? '' : opts.sourceEntityId;
    const sourceEntityType =
      opts.sourceEntityType === undefined ? '' : opts.sourceEntityType;
    const sourceEntityDomain =
      opts.sourceEntityDomain === undefined ? '' : opts.sourceEntityDomain;
    if (
      sourceEntityType === SUBMITTAL_ENTITIY_TYPE &&
      sourceEntityDomain === SUBMITTAL_ENTITIY_DOMAIN
    ) {
      return sourceEntityId;
    } else {
      return '';
    }
  } else {
    return '';
  }
}

const SubmittalsDataView: Exclude<
  ReferencePickerPlugin<SubmittalsItem>['CustomDataView'],
  undefined
> = ({
  entities = [],
  onLoadMore,
  disabledIds = [],
  selectedIds = [],
  multiple,
  onChange,
  setSelection,
  loading,
  error,
  onTryAgain,
  setSort,
  sort,
  dataViewProps,
}) => {
  const { t } = useTranslation();
  const sourceSubmittalEntity = extractDataViewProps(dataViewProps);
  const dataTableColumns = useTableColumns();
  const { customAttributes } = useCustomAttributes();
  const [disabledIdsWithSource, selectedIdsWithSource] = useMemo(
    () =>
      sourceSubmittalEntity !== ''
        ? [
            [...disabledIds, sourceSubmittalEntity],
            [...selectedIds, sourceSubmittalEntity],
          ]
        : [disabledIds, selectedIds],
    [disabledIds, selectedIds, sourceSubmittalEntity],
  );

  const dataByKey = useMemo(
    () => keyBy(entities, ENTITIES_MAPPER_KEY),
    [entities],
  );

  const columns = useMemo(
    () => [
      ...(multiple
        ? [
            {
              id: TABLE_COLUMN_ID_KEY.SELECT,
              size: 40,
              header: ({
                table: { getRowModel, getSelectedRowModel },
              }: CellContext<RowData, never>) => {
                const totalRows = getRowModel().rows.filter(
                  (row) => row.getCanSelect() || row.getIsSelected(),
                ).length;
                const selectedRows = getSelectedRowModel().rows.length;
                const isAllSelected = totalRows === selectedRows;
                const isSomeSelected =
                  selectedRows > 0 ? Checkbox.INDETERMINATE : false;
                return (
                  <Checkbox
                    checked={isAllSelected || isSomeSelected}
                    onChange={(checked) => {
                      const selectedIds = checked
                        ? entities.filter(
                            ({ id }) => !disabledIdsWithSource.includes(id),
                          )
                        : [];
                      setSelection(selectedIds, false);
                    }}
                  />
                );
              },
              cell: ({
                row: { getIsSomeSelected, getIsSelected, getCanSelect },
              }: CellContext<RowData, never>) => (
                <Checkbox
                  checked={
                    getIsSomeSelected()
                      ? Checkbox.INDETERMINATE
                      : getIsSelected()
                  }
                  disabled={!getCanSelect()}
                />
              ),
            },
          ]
        : []),
      ...dataTableColumns,
    ],
    [dataTableColumns, disabledIdsWithSource, entities, setSelection, multiple],
  );

  const sorting = useMemo(() => {
    return sort?.field
      ? [
          {
            id: sort.field.toString(),
            desc: sort?.order === SORT_ORDER.DESC,
          },
        ]
      : [];
  }, [sort]);

  const rowSelection = useMemo(
    () =>
      (selectedIdsWithSource || []).reduce(
        (acc: { [key: string]: boolean }, id) => {
          acc[id] = true;
          return acc;
        },
        {},
      ),
    [selectedIdsWithSource],
  );

  const onSortingChange = (updaterOrValue: Updater<SortingState>) => {
    const sortValue =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(sorting)
        : updaterOrValue;
    const { id, desc } = sortValue?.[0];
    setSort?.({
      field: getSortFieldId(id, customAttributes),
      order: desc ? SORT_ORDER.DESC : SORT_ORDER.ASC,
      columnName: id,
    });
  };

  const table = useReactTable({
    columns: columns as [],
    data: entities,
    getRowId: (row: SubmittalsItem) => row?.id,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row: Row<SubmittalsItem>) =>
      !disabledIdsWithSource?.includes(row.id),
    enableMultiRowSelection: multiple,
    enableColumnResizing: false,
    enableSortingRemoval: false,
    state: {
      sorting,
      rowSelection,
    },
    onSortingChange,
  });

  const {
    getTableRowProps,
    getTableCellProps,
    getTableHeaderGroupProps,
    getTableHeaderProps,
  } = useTable({
    table,
  });

  const { getCenterTotalSize, getRowModel, getHeaderGroups } = table;

  const { rows } = getRowModel();

  const { handleScroll, virtualRows, layouts, tableRef } = useVirtualTable({
    onLoadMore,
    rowsLength: rows?.length,
    loading,
    error: !!error,
  });

  const { paddingTop, paddingBottom } = layouts;

  const handleRowSelection = ({
    getCanSelect,
    getIsSelected,
    id,
  }: Row<SubmittalsItem>) => {
    if (!getCanSelect?.()) return;
    const selectedRow = dataByKey[id] as SubmittalsItem;
    if (multiple) {
      onChange({
        entity: selectedRow,
        checked: !getIsSelected?.(),
      });
    } else {
      setSelection(selectedIdsWithSource.includes(id) ? [] : [selectedRow]);
    }
  };

  return (
    <Box
      width="100%"
      height="100%"
      overflow="auto"
      onScroll={handleScroll}
      ref={tableRef}
    >
      <table style={STYLES.TableStyle(getCenterTotalSize())}>
        <thead style={STYLES.StickyHeader}>
          {getHeaderGroups()?.map((headerGroup) => (
            <TableHeaderGroup
              key={headerGroup.id}
              {...getTableHeaderGroupProps(headerGroup)}
            >
              {headerGroup.headers.map((header) => (
                <TableHeader key={header.id} {...getTableHeaderProps(header)} />
              ))}
            </TableHeaderGroup>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 && <TableRow style={{ height: `${paddingTop}px` }} />}
          {virtualRows.map(({ index }) => {
            const row = rows[index];
            const { getVisibleCells, id, getCanSelect } = row;
            return (
              <TableRow
                key={id}
                {...getTableRowProps(row)}
                onClick={() => handleRowSelection(row)}
                disabled={!getCanSelect()}
              >
                {getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} {...getTableCellProps(cell)} />
                ))}
              </TableRow>
            );
          })}
          {paddingBottom > 0 && (
            <TableRow style={{ height: `${paddingBottom}px` }} />
          )}
        </tbody>
      </table>
      {(loading || error) && (
        <LoadMoreDataFooter
          isLoading={loading}
          onLoad={async () => onLoadMore?.()}
          hasError={!!error}
          onRetry={async () => onTryAgain?.()}
          style={STYLES.LoadMoreDataRow}
          renderLoading={() => (
            <>
              <ProgressRing size={ProgressRing.SIZES.SMALL} />
              <Text
                typography="bodySmall"
                color="charcoal700"
                marginLeft="13px"
              >
                {t('LOADING_MORE')}
              </Text>
            </>
          )}
        />
      )}
    </Box>
  );
};

SubmittalsDataView.displayName = 'SubmittalsDataView';

export default SubmittalsDataView;
